import React, { forwardRef } from 'react';
import { Modal, Select, Button, Flex, Text, SelectItemProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/Icon';
import { LicenseData } from '../types/licenseData.interface';
import { useUpdateLicense } from '../hooks/useUpdateLicense';
import { useNotifications } from '../../../../hooks/utils/useNotifications';
import styles from './UpdateLicenseModal.module.scss';

interface UpdateLicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  license: LicenseData | null;
}

interface StatusOption {
  value: 'allowed' | 'review' | 'denied';
  label: string;
  icon: string;
}

export const UpdateLicenseModal: React.FC<UpdateLicenseModalProps> = ({
  isOpen,
  onClose,
  license,
}) => {
  const { t } = useTranslation();
  const updateLicenseMutation = useUpdateLicense();
  const { success: showSuccessNotification, error: showErrorNotification } =
    useNotifications();
  const [selectedStatus, setSelectedStatus] = React.useState<string>('');

  const alertStatusOptions: StatusOption[] = [
    {
      value: 'allowed',
      label: t('page.userSettings.licenses.status.approved'),
      icon: 'circle-check',
    },
    {
      value: 'review',
      label: t('page.userSettings.licenses.status.review'),
      icon: 'eye',
    },
    {
      value: 'denied',
      label: t('page.userSettings.licenses.status.forbidden'),
      icon: 'ban',
    },
  ];

  // Custom renderer for each Select item
  const SelectItem = forwardRef<HTMLDivElement, SelectItemProps & { icon: string }>(
    ({ icon, label, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex align="center" gap="xs">
          <Icon icon={icon as any} size="sm" className={styles.selectItemIcon} />
          <Text size="14px">{label}</Text>
        </Flex>
      </div>
    ),
  );

  // Reset state when modal opens/closes or license changes
  React.useEffect(() => {
    if (license && isOpen) {
      setSelectedStatus(license.alertStatus);
    } else {
      setSelectedStatus('');
    }
  }, [license, isOpen]);

  // Get the icon for the currently selected status
  const getSelectedIcon = () => {
    const selectedOption = alertStatusOptions.find(
      (option) => option.value === selectedStatus,
    );
    return selectedOption ? selectedOption.icon : undefined;
  };

  const handleSave = async () => {
    if (!license || !selectedStatus) return;

    try {
      await updateLicenseMutation.mutateAsync({
        licenseId: license._id,
        alertStatus: selectedStatus as 'allowed' | 'review' | 'denied',
      });

      showSuccessNotification({
        title: t(
          'page.userSettings.licenses.updateModal.notifications.updateSuccess.title',
        ),
        message: t(
          'page.userSettings.licenses.updateModal.notifications.updateSuccess.message',
        ),
      });

      onClose();
    } catch (error) {
      showErrorNotification({
        title: t(
          'page.userSettings.licenses.updateModal.notifications.updateError.title',
        ),
        message: t(
          'page.userSettings.licenses.updateModal.notifications.updateError.message',
        ),
      });
    }
  };

  const handleCancel = () => {
    setSelectedStatus(license?.alertStatus || '');
    onClose();
  };

  const customTitle = (
    <div>
      <Text className={styles.modalTitle}>
        {t('page.userSettings.licenses.updateModal.title')}
      </Text>
      <Text className={styles.modalText}>
        {t('page.userSettings.licenses.updateModal.subtitle', {
          licenseName: license?.fullName || license?.shortName,
        })}
      </Text>
    </div>
  );

  return (
    <Modal opened={isOpen} onClose={handleCancel} size="md" title={customTitle}>
      {license && (
        <>
          <Select
            withinPortal
            label={t('page.userSettings.licenses.updateModal.approvalStatusLabel')}
            placeholder={t(
              'page.userSettings.licenses.updateModal.approvalStatusPlaceholder',
            )}
            itemComponent={SelectItem}
            data={alertStatusOptions}
            value={selectedStatus}
            onChange={(val) => setSelectedStatus(val!)}
            searchable
            nothingFound={t('page.userSettings.licenses.updateModal.noOptions')}
            mb="xl"
            icon={
              getSelectedIcon() ? (
                <Icon
                  icon={getSelectedIcon() as any}
                  size="sm"
                  className={styles.selectItemIcon}
                />
              ) : undefined
            }
          />

          <Flex justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleCancel}>
              {t('page.userSettings.licenses.updateModal.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              loading={updateLicenseMutation.isLoading}
              disabled={!selectedStatus}
            >
              {t('page.userSettings.licenses.updateModal.save')}
            </Button>
          </Flex>
        </>
      )}
    </Modal>
  );
};
